import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, Phone, ArrowRight, Star, ChevronDown, ShieldCheck, Clock, MapPin, Sparkles, HardHat, Truck } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth, formatApiError } from "../lib/auth";
import { submitContactForm } from "../lib/api";

const SERVICES = [
    { title: "Excavation & Grading", body: "Site prep, foundation excavation, trenching, and grading to spec.", img: "/images/services/excavation.jpg", slug: "excavation", icon: HardHat },
    { title: "Demolition", body: "Full-building demolition, deconstruction, asbestos removal, clean-up.", img: "/images/services/demolition.jpg", slug: "demolition", icon: Truck },
    { title: "Septic Services", body: "Inspection, design, installation, and long-term maintenance.", img: "/images/services/septic.jpg", slug: "septic", icon: ShieldCheck },
    { title: "Site Services", body: "End-to-end site work — one crew handles the whole scope.", img: "/images/services/site.jpg", slug: "site", icon: Sparkles },
];

const STATS = [
    { n: "500+", l: "Projects delivered" },
    { n: "4+", l: "Years serving Simcoe" },
    { n: "5.0★", l: "Average customer rating" },
    { n: "24 h", l: "Typical quote turnaround" },
];

const REASONS = [
    { title: "Licensed & insured", body: "Fully OOWA-approved and compliant with every local, provincial, and federal regulation.", icon: ShieldCheck },
    { title: "On time, on budget", body: "We attend the site, quote in detail, and never chase extras after the fact.", icon: Clock },
    { title: "Local specialists", body: "Simcoe County born and operated — we know the soils, the codes, and the crews.", icon: MapPin },
];

export default function LandingPage() {
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    // If a saved session exists, skip the marketing landing and jump
    // straight into the app. Logged-in users can still reach the marketing
    // site via the "View site" link inside the app.
    useEffect(() => {
        if (!loading && user) {
            navigate("/home", { replace: true });
        }
    }, [user, loading, navigate]);

    const [c, setC] = useState({ name: "", email: "", phone: "", message: "", website: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const setField = (k) => (e) => setC((prev) => ({ ...prev, [k]: e.target.value }));

    const handleContact = async (e) => {
        e.preventDefault();
        if (sending) return;
        if (!c.name.trim() || !c.email.trim() || !c.message.trim()) {
            toast.error("Name, email, and message are required.");
            return;
        }
        setSending(true);
        try {
            await submitContactForm(c);
            setSent(true);
            setC({ name: "", email: "", phone: "", message: "", website: "" });
            toast.success("Message sent — we'll be in touch soon.");
        } catch (err) {
            toast.error(formatApiError(err) || "Could not send your message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <MarketingShell testid="landing-page">
            {/* ─────────── HERO ─────────── */}
            <section className="hero-dark relative h-[80vh] min-h-[560px] flex items-center text-white overflow-hidden" data-testid="landing-hero">
                <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: "url(/images/services/excavation.jpg)" }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-900/70 to-slate-950/85" aria-hidden="true" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(10,17,40,0.6)_100%)]" aria-hidden="true" />

                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00A3FF]/15 border border-[#00A3FF]/40 backdrop-blur-sm mb-6">
                            <span className="w-2 h-2 rounded-full bg-[#00A3FF] animate-pulse" />
                            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#7DD3FC]">Now booking Spring 2026 projects</span>
                        </div>
                        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
                            Precision excavation<br />
                            <span className="text-[#00A3FF]">for Simcoe County.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-xl text-slate-200 leading-relaxed">
                            Locally owned. Fully insured. Every project quoted at the site, priced honestly, and delivered on time.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <a href="tel:7053334376" data-testid="landing-hero-call" className="inline-flex items-center gap-2 px-6 py-4 rounded-md bg-[#007AFF] hover:bg-[#0369A1] font-bold text-base shadow-lg shadow-[#007AFF]/30 hover:shadow-xl hover:shadow-[#007AFF]/40 transition-all">
                                <Phone className="w-5 h-5" /> Call 705-333-4376
                            </a>
                            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-4 rounded-md border border-slate-400/60 backdrop-blur-sm text-white font-bold hover:bg-white/10 transition-colors">
                                Get a free quote <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </div>

                <a href="#stats" className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/60 hover:text-white transition-colors" aria-label="Scroll down">
                    <ChevronDown className="w-8 h-8 animate-bounce" />
                </a>
            </section>

            {/* ─────────── STATS STRIP ─────────── */}
            <section id="stats" className="hero-dark bg-[#0A1128] text-white py-10 border-b border-slate-800">
                <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-4 text-center">
                    {STATS.map((s) => (
                        <div key={s.l} className="border-r last:border-r-0 sm:border-slate-800 border-transparent px-2">
                            <div className="text-3xl sm:text-4xl font-bold text-[#00A3FF] tracking-tight">{s.n}</div>
                            <div className="mt-1 text-[11px] uppercase tracking-[0.15em] text-slate-400 font-bold">{s.l}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ─────────── SERVICES ─────────── */}
            <section id="services" className="py-20 bg-slate-50 scroll-mt-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="max-w-2xl">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] font-bold">What we do</p>
                        <h2 className="mt-3 text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight">Four services. One crew.</h2>
                        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                            End-to-end site work across Barrie and Simcoe County. No subcontractor shuffle, no surprise change orders.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {SERVICES.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-white rounded-xl overflow-hidden border border-slate-200 hover:border-[#007AFF] hover:shadow-2xl hover:-translate-y-1 transition-all duration-300" data-testid={`landing-service-${s.slug}`}>
                                    <div className="relative h-48 bg-slate-800 overflow-hidden">
                                        <img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                                        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2 text-white">
                                            <Icon className="w-5 h-5 text-[#00A3FF]" />
                                            <span className="text-xs uppercase tracking-widest font-bold">Service</span>
                                        </div>
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-bold text-slate-900 text-lg group-hover:text-[#007AFF] transition-colors">{s.title}</h3>
                                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                                        <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#007AFF] group-hover:gap-2 transition-all">
                                            Learn more <ArrowRight className="w-4 h-4" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ─────────── WHY US ─────────── */}
            <section className="py-20 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <div className="relative">
                        <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-slate-800 shadow-2xl">
                            <img src="/images/services/septic.jpg" alt="Steeltooth crew at work" className="w-full h-full object-cover" />
                        </div>
                        <div className="hidden sm:block absolute -bottom-6 -right-6 bg-[#007AFF] text-white px-6 py-5 rounded-xl shadow-xl max-w-[220px]">
                            <div className="text-3xl font-bold">Est. 2021</div>
                            <div className="text-xs uppercase tracking-widest mt-1 opacity-90">Locally owned & operated</div>
                        </div>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] font-bold">Why Steeltooth</p>
                        <h2 className="mt-3 text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                            Straightforward quotes.<br />Zero surprises.
                        </h2>
                        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                            We don't overprice to leave room, and we don't underprice hoping for extras. Every quote is built at your site using a detailed takeoff — so what you sign is what you pay.
                        </p>
                        <div className="mt-8 space-y-4">
                            {REASONS.map((r) => {
                                const Icon = r.icon;
                                return (
                                    <div key={r.title} className="flex items-start gap-4">
                                        <div className="shrink-0 w-11 h-11 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center">
                                            <Icon className="w-5 h-5 text-[#007AFF]" />
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-900">{r.title}</div>
                                            <div className="text-sm text-slate-600 mt-0.5 leading-relaxed">{r.body}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-8 flex gap-3">
                            <Link to="/about" className="hero-dark inline-flex items-center gap-2 px-5 py-3 rounded-md bg-slate-900 hover:bg-slate-800 text-white font-bold transition-colors" data-testid="landing-cta-about">
                                Read our story <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link to="/portfolio" className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-slate-300 text-slate-800 font-bold hover:border-[#007AFF] hover:text-[#007AFF] transition-colors" data-testid="landing-cta-portfolio">
                                See recent work
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─────────── TESTIMONIAL ─────────── */}
            <section className="hero-dark relative bg-slate-900 text-white py-20 overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/images/services/demolition.jpg)" }} aria-hidden="true" />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900" aria-hidden="true" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <div className="inline-flex items-center gap-1 mb-6">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className="w-6 h-6 fill-[#FFB020] text-[#FFB020]" />
                        ))}
                    </div>
                    <blockquote className="text-2xl sm:text-3xl font-medium leading-relaxed text-slate-100">
                        “Corey was a great to deal with. He came when he said he would arrive and delivered his work on time. A true businessman. His attention to detail is commendable, his employees worked well with everyone.”
                    </blockquote>
                    <div className="mt-8 text-sm uppercase tracking-[0.2em] text-[#00A3FF] font-bold">
                        Doug McQuigge — Simcoe County
                    </div>
                </div>
            </section>

            {/* ─────────── CONTACT CTA ─────────── */}
            <section id="contact" className="py-20 bg-slate-50 scroll-mt-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-2">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] font-bold">Get in touch</p>
                        <h2 className="mt-3 text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                            Ready to talk about your project?
                        </h2>
                        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                            One business day, tops. We'll set up a site walk and have your quote back the same week.
                        </p>
                        <div className="mt-8 space-y-3">
                            <a href="tel:7053334376" className="flex items-center gap-3 p-4 rounded-lg bg-white border border-slate-200 hover:border-[#007AFF] transition-colors group">
                                <Phone className="w-5 h-5 text-[#007AFF] group-hover:scale-110 transition-transform" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Call</div>
                                    <div className="font-bold text-slate-900">705-333-4376</div>
                                </div>
                            </a>
                            <div className="flex items-center gap-3 p-4 rounded-lg bg-white border border-slate-200">
                                <MapPin className="w-5 h-5 text-[#007AFF]" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Service area</div>
                                    <div className="font-bold text-slate-900">Barrie & Simcoe County</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleContact} className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-xl p-6 sm:p-8 space-y-4" data-testid="landing-contact-form" noValidate>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <FormField label="Your name" required>
                                <Input value={c.name} onChange={setField("name")} placeholder="Jane Contractor" data-testid="landing-contact-name" className="bg-white border-slate-300 h-11" required />
                            </FormField>
                            <FormField label="Phone (optional)">
                                <Input value={c.phone} onChange={setField("phone")} inputMode="tel" placeholder="705-000-0000" data-testid="landing-contact-phoneinput" className="bg-white border-slate-300 h-11" />
                            </FormField>
                        </div>
                        <FormField label="Email" required>
                            <Input type="email" inputMode="email" value={c.email} onChange={setField("email")} placeholder="you@company.com" data-testid="landing-contact-emailinput" className="bg-white border-slate-300 h-11" required />
                        </FormField>
                        <FormField label="Tell us about your project" required>
                            <Textarea value={c.message} onChange={setField("message")} placeholder="Site address, scope, timing…" rows={5} data-testid="landing-contact-message" className="bg-white border-slate-300" required />
                        </FormField>
                        <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                            <label>Website<input tabIndex={-1} autoComplete="off" value={c.website} onChange={setField("website")} /></label>
                        </div>
                        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                            <div className="text-xs text-slate-500" data-testid="landing-contact-status">
                                {sent ? "Thanks — we'll be in touch shortly." : "We reply within one business day."}
                            </div>
                            <Button type="submit" disabled={sending} data-testid="landing-contact-submit" className="h-12 px-6 bg-[#007AFF] hover:bg-[#0369A1] text-white font-bold text-base shadow-lg shadow-[#007AFF]/25">
                                {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>) : (<><Send className="w-4 h-4 mr-2" />Send message</>)}
                            </Button>
                        </div>
                    </form>
                </div>
            </section>
        </MarketingShell>
    );
}

function FormField({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                {label}{required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {children}
        </div>
    );
}
